import { useState, useEffect, useMemo } from 'react';
import { useDashboard, useInvoices, useLosses } from '../../hooks/useData';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Send, AlertTriangle, TrendingUp, TrendingDown, Wallet, ArrowUp, ArrowDown, Target, ShoppingCart, CalendarDays, Activity, Utensils, ChevronLeft, ChevronRight, Download, Percent, Snowflake, CircleDot, Plus, Trash2, Users, Loader2, Check, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line } from 'recharts';
import OptimizeButton from '../../components/shared/OptimizeButton';

/* ── Sparkline mini-chart ── */
const Sparkline = ({ data, color = 'rgb(var(--lm-gold))', height = 32 }) => {
  if (!data || data.length < 2) return null;
  return (
    <div className="mt-2 opacity-60">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spark-${color.replace(/[^a-z0-9]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#spark-${color.replace(/[^a-z0-9]/g, '')})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── KPI Card ── */
const KpiCard = ({ label, value, sub, icon: Icon, color, sparkData, delay = 0, testId }) => (
  <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }} data-testid={testId}>
    <div className="flex items-start justify-between mb-1">
      <span className="kpi-label">{label}</span>
      {Icon && <Icon size={14} strokeWidth={1.5} style={{ color }} className="opacity-50" />}
    </div>
    <p className="kpi-value text-2xl" style={{ color }}>{value}</p>
    {sub && <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mt-1">{sub}</p>}
    {sparkData && <Sparkline data={sparkData} color={color} />}
  </div>
);

/* ── Delta badge ── */
const Delta = ({ value, invert = false }) => {
  if (value == null) return null;
  const positive = invert ? value <= 0 : value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-mono px-1.5 py-0.5 rounded-sm ${
      positive ? 'text-[rgb(var(--lm-green))] bg-[rgb(var(--lm-green)/0.08)]' : 'text-[rgb(var(--lm-red))] bg-[rgb(var(--lm-red)/0.08)]'
    }`}>
      {value >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
      {Math.abs(value)}%
    </span>
  );
};

const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/* ── Frais fixes detail accordion ── */
const RepartitionFixesDetail = ({ categories }) => {
  const [open, setOpen] = useState(false);
  if (!categories || categories.length === 0) return null;
  return (
    <div className="-mt-1 ml-5">
      <button onClick={() => setOpen(!open)} className="text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))] flex items-center gap-1" data-testid="fixes-detail-toggle">
        Detail {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-2 border-l-2 border-orange-500/20">
          {categories.map((c, i) => (
            <div key={i} className="flex justify-between text-[11px]">
              <span className="text-[rgb(var(--lm-text-muted))]">{c.categorie}</span>
              <span className="font-mono text-[rgb(var(--lm-text-muted))]">{c.montant.toLocaleString('fr-FR')} EUR ({c.pct}%)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Charge row with target comparison ── */
const ChargeRow = ({ label, value, pct, target, color }) => {
  const ok = pct <= target;
  const ecart = Math.abs(pct - target);
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
          <span className="text-sm text-[rgb(var(--lm-text-secondary))]">{label}</span>
        </div>
        <div className="text-right flex items-center gap-2">
          <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(value)}</span>
          <span className={`text-xs font-mono font-medium ${ok ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>{pct}%</span>
        </div>
      </div>
      {/* Progress bar with target marker */}
      <div className="ml-5 mt-1 relative">
        <div className="h-1.5 rounded-full bg-[rgb(var(--lm-border)/0.3)] overflow-hidden">
          <div className={`h-full rounded-full transition-all ${ok ? 'bg-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-red))]'}`} style={{ width: `${Math.min(pct / (target * 1.5) * 100, 100)}%` }} />
        </div>
        <div className="absolute top-0 h-1.5 w-0.5 bg-[rgb(var(--lm-gold))]" style={{ left: `${target / (target * 1.5) * 100}%` }} title={`Objectif: ${target}%`} />
        <div className="flex justify-between mt-0.5">
          <span className={`text-[9px] ${ok ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
            {ok ? `${ecart > 0 ? `-${ecart.toFixed(1)}` : '='}` : `+${ecart.toFixed(1)}`} vs obj.
          </span>
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">obj. {target}%</span>
        </div>
      </div>
    </div>
  );
};

/* ── Saisie salaires inline ── */
const SalairesForm = ({ onSaved }) => {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNom, setNewNom] = useState('');
  const [newNet, setNewNet] = useState('');
  const [newCharges, setNewCharges] = useState('42');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/salaries').then(r => setSalaries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const addSalary = async () => {
    if (!newNom.trim() || !newNet) return;
    setSaving(true);
    try {
      const res = await api.post('/salaries', { nom: newNom, salaire_net: parseFloat(newNet), taux_charges: parseFloat(newCharges || 42) });
      setSalaries(prev => [...prev, res.data]);
      setNewNom(''); setNewNet('');
      toast.success('Salaire ajoute');
      if (onSaved) onSaved();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const deleteSalary = async (id) => {
    try {
      await api.delete(`/salaries/${id}`);
      setSalaries(prev => prev.filter(s => s.id !== id));
      toast.success('Supprime');
      if (onSaved) onSaved();
    } catch { toast.error('Erreur'); }
  };

  const updateSalary = async (id, field, value) => {
    try {
      await api.put(`/salaries/${id}`, { [field]: parseFloat(value) });
      if (onSaved) onSaved();
    } catch { toast.error('Erreur'); }
  };

  const totalBrut = salaries.reduce((sum, s) => {
    const net = s.salaire_net || 0;
    const charges = s.taux_charges || 42;
    return sum + net + (net * charges / 100);
  }, 0);

  if (loading) return <div className="flex justify-center py-4"><Loader2 size={14} className="animate-spin text-purple-400" /></div>;

  return (
    <div className="space-y-2" data-testid="salaires-form">
      {salaries.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={12} className="text-amber-400" />
            <span className="text-xs font-medium text-amber-300">Aucun salaire saisi</span>
          </div>
          <p className="text-[10px] text-amber-400/70">Ajoutez vos salaires pour calculer le pourcentage charges/CA</p>
        </div>
      )}

      {salaries.map(s => {
        const net = s.salaire_net || 0;
        const charges = s.taux_charges || 42;
        const brut = net + (net * charges / 100);
        return (
          <div key={s.id} className="flex items-center gap-2 bg-[rgb(var(--lm-surface))] rounded-lg px-3 py-2" data-testid={`salary-${s.id}`}>
            <Users size={12} className="text-purple-400 shrink-0" />
            <span className="text-xs text-[rgb(var(--lm-text))] flex-1 truncate">{s.nom}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <input type="number" defaultValue={net} onBlur={e => updateSalary(s.id, 'salaire_net', e.target.value)}
                className="w-16 text-xs font-mono bg-transparent border border-[rgb(var(--lm-border)/0.3)] rounded px-1.5 py-0.5 text-right text-[rgb(var(--lm-text))]" />
              <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">net</span>
              <span className="text-[10px] font-mono text-purple-400">{Math.round(brut)}EUR</span>
              <button onClick={() => deleteSalary(s.id)} className="p-0.5 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-red))]"><Trash2 size={10} /></button>
            </div>
          </div>
        );
      })}

      {/* Ajouter */}
      <div className="flex gap-1.5 items-end">
        <div className="flex-1">
          <Input value={newNom} onChange={e => setNewNom(e.target.value)} placeholder="Nom employe"
            className="text-xs h-7 bg-transparent border-[rgb(var(--lm-border)/0.3)]" data-testid="salary-new-nom" />
        </div>
        <div className="w-20">
          <Input type="number" value={newNet} onChange={e => setNewNet(e.target.value)} placeholder="Net"
            className="text-xs h-7 font-mono bg-transparent border-[rgb(var(--lm-border)/0.3)]" data-testid="salary-new-net" />
        </div>
        <Button onClick={addSalary} disabled={saving || !newNom.trim() || !newNet} size="sm" className="h-7 bg-purple-500 hover:bg-purple-600 text-white px-2" data-testid="salary-add-btn">
          <Plus size={12} />
        </Button>
      </div>

      {salaries.length > 0 && (
        <div className="flex items-center justify-between pt-2 border-t border-[rgb(var(--lm-border)/0.2)]">
          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{salaries.length} employe{salaries.length > 1 ? 's' : ''} (charges ~42%)</span>
          <span className="text-xs font-mono font-bold text-purple-400">{formatCurrency(totalBrut)} /mois</span>
        </div>
      )}
    </div>
  );
};


/* ── Rapport Mensuel ── */
const RapportMensuel = ({ mois, moisLabel }) => {
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadRapport = async () => {
    if (rapport) { setOpen(!open); return; }
    setLoading(true);
    try {
      const r = await api.get(`/rapport-mensuel/${mois}`);
      setRapport(r.data);
      setOpen(true);
    } catch { toast.error('Erreur chargement rapport'); }
    setLoading(false);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/rapport-mensuel/${mois}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_lemas_${mois}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch { toast.error('Erreur telechargement PDF'); }
    setDownloading(false);
  };

  // Reset when month changes
  useEffect(() => { setRapport(null); setOpen(false); }, [mois]);

  return (
    <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '320ms', animationFillMode: 'backwards' }} data-testid="rapport-mensuel">
      <div className="flex items-center justify-between">
        <button onClick={loadRapport} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="rapport-toggle">
          <FileText size={14} className="text-[rgb(var(--lm-gold))]" />
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Rapport mensuel</h3>
          {loading ? <Loader2 size={12} className="animate-spin text-[rgb(var(--lm-gold))]" /> : open ? <ChevronUp size={12} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={12} className="text-[rgb(var(--lm-text-muted))]" />}
        </button>
        <Button onClick={downloadPdf} size="sm" variant="outline" disabled={downloading} className="h-7 text-[10px] gap-1" data-testid="rapport-pdf-btn">
          {downloading ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
          PDF
        </Button>
      </div>

      {open && rapport && (
        <div className="mt-4 space-y-4">
          {/* KPIs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))]">
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">CA HT</p>
              <p className="font-mono text-sm font-bold text-[rgb(var(--lm-gold))]">{formatCurrency(rapport.ca_ht)}</p>
              <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">{rapport.pct_realisation}% de l'objectif</p>
            </div>
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))]">
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Couverts</p>
              <p className="font-mono text-sm font-bold text-[rgb(var(--lm-text))]">{rapport.couverts}</p>
              <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">{rapport.jours_actifs} jours</p>
            </div>
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))]">
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Ticket moyen</p>
              <p className="font-mono text-sm font-bold text-[rgb(var(--lm-text))]">{formatCurrency(rapport.ticket_moyen)}</p>
            </div>
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))]">
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Evolution</p>
              <p className={`font-mono text-sm font-bold ${rapport.evolution_ca >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                {rapport.evolution_ca >= 0 ? '+' : ''}{rapport.evolution_ca}%
              </p>
              <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">vs mois prec.</p>
            </div>
          </div>

          {/* Repartition recap */}
          <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))]">
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase mb-2">Repartition / objectif {formatCurrency(rapport.objectif_ca)}</p>
            <div className="space-y-1.5">
              {[
                { label: 'Matieres', val: rapport.achats_ht, pct: rapport.pct_matieres, target: 30, color: 'bg-blue-500' },
                { label: 'Salaires', val: rapport.masse_salariale, pct: rapport.pct_salaires, target: 35, color: 'bg-purple-500' },
                { label: 'Frais fixes', val: rapport.frais_fixes, pct: rapport.pct_fixes, target: 25, color: 'bg-orange-500' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  <span className="text-[rgb(var(--lm-text-secondary))] w-20">{item.label}</span>
                  <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(item.val)}</span>
                  <span className={`font-mono ml-auto ${item.pct <= item.target ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>{item.pct}%</span>
                  <span className="text-[rgb(var(--lm-text-muted))] text-[9px]">obj. {item.target}%</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-[11px] pt-1 border-t border-[rgb(var(--lm-border)/0.2)]">
                <div className={`w-2 h-2 rounded-full ${rapport.pct_resultat >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span className="font-medium text-[rgb(var(--lm-text))] w-20">Resultat</span>
                <span className={`font-mono font-bold ${rapport.pct_resultat >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>{formatCurrency(rapport.resultat_net)}</span>
                <span className={`font-mono ml-auto font-bold ${rapport.pct_resultat >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>{rapport.pct_resultat}%</span>
              </div>
            </div>
          </div>

          {/* Analyse IA */}
          {rapport.analyse_ia && (
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-gold)/0.2)]">
              <p className="text-[9px] text-[rgb(var(--lm-gold))] uppercase mb-2 flex items-center gap-1">
                <Activity size={10} /> Analyse IA
              </p>
              <div className="text-[11px] text-[rgb(var(--lm-text-secondary))] whitespace-pre-line leading-relaxed">
                {rapport.analyse_ia}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const PatronDashboard = ({ setActiveTab }) => {
  const [mois, setMois] = useState(getCurrentMonth());
  const { data: dashboard, loading, refetch } = useDashboard(mois);
  const { invoices } = useInvoices(mois);
  const { losses } = useLosses(mois);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [bilanData, setBilanData] = useState(null);
  const [objectifCA, setObjectifCA] = useState(0);
  const [budgetCuisine, setBudgetCuisine] = useState(0);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [n1Data, setN1Data] = useState(null);
  const [ratioData, setRatioData] = useState(null);
  const [ratioBim, setRatioBim] = useState(null);
  const [persoData, setPersoData] = useState(null);
  const [ratioPatron, setRatioPatron] = useState(30);
  const [ratioChef, setRatioChef] = useState(20);
  const [repartition, setRepartition] = useState(null);
  const [showSalaires, setShowSalaires] = useState(false);
  const [editingObjectif, setEditingObjectif] = useState(false);
  const [pendingAccessCount, setPendingAccessCount] = useState(0);

  const navigateMonth = (dir) => {
    const [yy, mm] = mois.split('-').map(Number);
    let nm = mm + dir, ny = yy;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const [yy, mm] = mois.split('-').map(Number);

  useEffect(() => {
    if (dashboard) {
      setObjectifCA(dashboard.objectif_ca || 0);
      setBudgetCuisine(dashboard.budget_cuisine || 0);
      setRatioPatron(dashboard.ratio_cible || 30);
      setRatioChef(dashboard.ratio_chef || 20);
    }
  }, [dashboard]);

  useEffect(() => {
    api.get(`/budget/${mois}/historique`).then(r => setBudgetHistory(r.data)).catch(() => {});
    api.get(`/ratio-matieres/${mois}`).then(r => setRatioData(r.data)).catch(() => setRatioData(null));
    api.get(`/ratio-bimestriel/${mois}`).then(r => setRatioBim(r.data)).catch(() => setRatioBim(null));
    api.get(`/repartition-charges/${mois}`).then(r => setRepartition(r.data)).catch(() => setRepartition(null));
    api.get('/auth/users').then(r => {
      const pending = r.data.filter(u => u.status === 'pending');
      setPendingAccessCount(pending.length);
    }).catch(() => {});
  }, [mois]);

  useEffect(() => {
    const load = async () => {
      setSummaryLoading(true);
      try {
        const [sumRes, bilanRes, n1Res] = await Promise.all([
          api.get(`/ai/daily-summary/${mois}`),
          api.get(`/bilan-reel/${mois.split('-')[0]}`),
          api.get(`/comparaison-n1/${mois}`)
        ]);
        setSummary(sumRes.data);
        setBilanData(bilanRes.data);
        setN1Data(n1Res.data);
      } catch (e) { console.error(e); }
      finally { setSummaryLoading(false); }
    };
    load();
  }, [mois]);

  const handleTransmitBudget = async () => {
    if (!budgetCuisine || budgetCuisine <= 0) { toast.error('Entrez un budget courses'); return; }
    try {
      await api.post('/budget', { mois, budget_cuisine: budgetCuisine, objectif_ca: objectifCA });
      toast.success(`Budget transmis au chef: ${formatCurrency(budgetCuisine)}`);
      refetch();
      api.get(`/budget/${mois}/historique`).then(r => setBudgetHistory(r.data)).catch(() => {});
    } catch { toast.error('Erreur'); }
  };

  const handleSaveObjectif = async () => {
    if (!objectifCA || objectifCA <= 0) { toast.error('Entrez un objectif CA'); return; }
    try {
      await api.post('/budget', { mois, budget_cuisine: 0, objectif_ca: objectifCA });
      await api.post('/budget/ratios', { mois, ratio_reel: ratioPatron, ratio_chef: ratioChef });
      toast.success('Objectif & ratios sauvegardes — budgets recalcules');
      refetch();
      api.get(`/budget/${mois}/historique`).then(r => setBudgetHistory(r.data)).catch(() => {});
    } catch { toast.error('Erreur'); }
  };

  const handleSaveRatios = async (newPatron, newChef) => {
    try {
      await api.post('/budget/ratios', { mois, ratio_reel: newPatron, ratio_chef: newChef });
      if (objectifCA > 0) {
        await api.post('/budget', { mois, budget_cuisine: 0, objectif_ca: objectifCA });
      }
      toast.success(`Ratios mis a jour : Patron ${newPatron}% / Chef ${newChef}%`);
      refetch();
      api.get(`/budget/${mois}/historique`).then(r => setBudgetHistory(r.data)).catch(() => {});
    } catch { toast.error('Erreur'); }
  };

  const caChartData = bilanData?.months?.map(m => ({
    mois: m.mois.slice(0, 3),
    ca: Math.round(m.ca_ht / 1000),
    depenses: Math.round((m.depenses_chef + m.charges_mensuelles) / 1000),
    resultat: Math.round(m.resultat / 1000),
  })).filter(m => m.ca > 0) || [];

  // Sparkline data from bilan
  const caSparkData = caChartData.map(d => ({ v: d.ca }));
  const resSparkData = caChartData.map(d => ({ v: Math.max(0, d.resultat) }));

  const depensesPie = summary ? [
    { name: 'Achats', value: summary.actuel.achats, color: 'rgb(var(--lm-gold))' },
    { name: 'Salaires', value: summary.actuel.charges * 0.52, color: 'rgb(var(--lm-green))' },
    { name: 'Fixes', value: summary.actuel.charges * 0.48, color: '#5B8FE0' },
    { name: 'Pertes', value: summary.actuel.pertes, color: 'rgb(var(--lm-red))' },
  ].filter(d => d.value > 0) : [];

  const a = summary?.actuel;
  const p = summary?.prediction;

  if (loading && summaryLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
        <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
      </div>
    );
  }

  const progressPct = objectifCA > 0 && a ? Math.min(100, (a.ca_ht / objectifCA) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="patron-dashboard">
      {/* ── Header ── */}
      <div className="flex items-end justify-between animate-slide-up" style={{ animationDelay: '0ms' }}>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Tableau de bord</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))]">{MONTHS_FR[mm]} {yy}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="dashboard-month-prev">
              <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
            </button>
            <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="dashboard-month-next">
              <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
            </button>
          </div>
          <button onClick={() => { refetch(); setSummaryLoading(true); api.get(`/ai/daily-summary/${mois}`).then(r => { setSummary(r.data); setSummaryLoading(false); }); }}
            className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors p-2"
            data-testid="refresh-btn">
            <RefreshCw size={16} strokeWidth={1.5} />
          </button>
          <OptimizeButton page="dashboard" mois={mois} />
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/export/${mois}`;
              link.download = `LeMas_${mois}.xlsx`;
              link.click();
              toast.success('Export Excel en cours...');
            }}
            className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors p-2"
            data-testid="export-excel-btn"
            title="Exporter en Excel"
          >
            <Download size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="CA HT Total" value={a ? formatCurrency(a.ca_ht) : '-'}
          sub={(a?.ca_traiteur || a?.ca_traiteur_ht) > 0 ? `Salle ${formatCurrency(a.ca_ht - (a.ca_traiteur || a.ca_traiteur_ht))} + 🥂 ${formatCurrency(a.ca_traiteur || a.ca_traiteur_ht)}` : (a?.nb_jours_saisis > 0 ? `${a.nb_jours_saisis}j | ${formatCurrency(a.ca_jour_moyen)}/j` : undefined)}
          icon={TrendingUp} color="rgb(var(--lm-gold))" sparkData={caSparkData} delay={0} testId="kpi-ca" />
        <KpiCard label="Resultat" value={a ? formatCurrency(a.resultat) : '-'}
          sub={a ? `Marge ${a.marge_pct}%` : undefined}
          icon={Activity} color={a && a.resultat >= 0 ? 'rgb(var(--lm-green))' : 'rgb(var(--lm-red))'} sparkData={resSparkData} delay={60} testId="kpi-resultat" />
        <KpiCard label="Achats HT" value={a ? formatCurrency(a.achats) : '-'}
          sub={a ? `TTC: ${formatCurrency(a.achats_ttc)} | ${a.nb_factures} fact.` : undefined}
          icon={ShoppingCart} color="rgb(var(--lm-orange))" delay={120} testId="kpi-achats" />
        <KpiCard label="Couverts" value={a?.couverts || 0}
          sub={a?.ticket_moyen > 0 ? `TM: ${formatCurrency(a.ticket_moyen)}` : undefined}
          icon={Utensils} color="rgb(var(--lm-text))" delay={180} testId="kpi-couverts" />
      </div>

      {/* CA Traiteur — désormais inclus dans le KPI "CA HT Total" ci-dessus */}

      {/* ── Prediction banner ── */}
      {p && a?.nb_jours_saisis > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }} data-testid="prediction-banner">
          <div className="flex items-center gap-2 mb-4">
            <Target size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
            <span className="text-xs tracking-[0.15em] uppercase" style={{ color: 'rgb(var(--lm-gold))' }}>
              Objectif {summary?.mois} (moy. historique)
            </span>
            {p.jours_restants > 0 && <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">({p.jours_restants}j restants)</span>}
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[rgb(var(--lm-text-muted))]">{(p.pct_atteint || 0).toFixed(0)}% atteint</span>
              <span className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(a.ca_ht)} / {formatCurrency(p.objectif_ht)}</span>
            </div>
            <div className="w-full h-2 rounded-full bg-[rgb(var(--lm-border))]">
              <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${Math.min(100, p.pct_atteint || 0)}%`,
                background: (p.pct_atteint || 0) >= 100 ? 'rgb(var(--lm-green))' : (p.pct_atteint || 0) >= 70 ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-red))'
              }} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Il manque</p>
              <p className={`font-mono text-xl ${p.il_manque_ht > 0 ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-green))]'}`}>
                {p.il_manque_ht > 0 ? formatCurrency(p.il_manque_ht) : 'Objectif atteint !'}
              </p>
            </div>
            {p.jours_restants > 0 && p.il_manque_ht > 0 && (
              <div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">A faire/jour</p>
                <p className="font-mono text-xl text-[rgb(var(--lm-orange))]">{formatCurrency(p.par_jour_restant)}</p>
              </div>
            )}
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Resultat prevu</p>
              <p className={`font-mono text-xl ${p.resultat_prevu >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>{formatCurrency(p.resultat_prevu)}</p>
            </div>
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Marge prevue</p>
              <p className={`font-mono text-xl ${p.marge_prevue >= 10 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-orange))]'}`}>{p.marge_prevue}%</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending Access Alert ── */}
      {pendingAccessCount > 0 && (
        <button onClick={() => setActiveTab('users')}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 hover:bg-amber-500/20 transition-all text-left animate-fade-in"
          data-testid="pending-access-alert">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 animate-pulse">
            <Users size={14} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{pendingAccessCount} demande{pendingAccessCount > 1 ? 's' : ''} d'acces en attente</p>
            <p className="text-[10px] opacity-70">Cliquez pour valider dans Outils &gt; Acces</p>
          </div>
          <ArrowUp size={14} className="rotate-90 opacity-50" />
        </button>
      )}

      {/* ── Alerts ── */}
      {summary?.alertes?.length > 0 && (
        <div className="space-y-2 animate-fade-in" data-testid="dashboard-alerts">
          {summary.alertes.map((alert, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-sm text-sm ${
              alert.type === 'danger' ? 'alert-card alert-danger' :
              alert.type === 'warning' ? 'alert-card alert-warning' :
              'alert-card alert-success'
            }`}>
              <AlertTriangle size={14} strokeWidth={1.5} />
              <span>{alert.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Quick Actions ── */}
      <div className="animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }}>
        <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Acces rapide</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="quick-actions">
          <button onClick={() => setActiveTab('saisie-ca')}
            className="group p-3 rounded-xl bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-gold))]/50 hover:shadow-sm transition-all text-left"
            data-testid="quick-saisie-ca">
            <div className="w-7 h-7 rounded-lg bg-[rgb(var(--lm-gold))]/10 flex items-center justify-center mb-2 group-hover:bg-[rgb(var(--lm-gold))]/20 transition-colors">
              <CalendarDays size={14} className="text-[rgb(var(--lm-gold))]" />
            </div>
            <p className="text-[11px] font-medium text-[rgb(var(--lm-text))]">Saisie CA</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">Chiffre du jour</p>
          </button>
          <button onClick={() => setActiveTab('invoices')}
            className="group p-3 rounded-xl bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-green))]/50 hover:shadow-sm transition-all text-left"
            data-testid="quick-scan-facture">
            <div className="w-7 h-7 rounded-lg bg-[rgb(var(--lm-green))]/10 flex items-center justify-center mb-2 group-hover:bg-[rgb(var(--lm-green))]/20 transition-colors">
              <ShoppingCart size={14} className="text-[rgb(var(--lm-green))]" />
            </div>
            <p className="text-[11px] font-medium text-[rgb(var(--lm-text))]">Scan Factures</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{a?.nb_factures || 0} ce mois</p>
          </button>
          <button onClick={() => setActiveTab('fixes')}
            className="group p-3 rounded-xl bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-orange))]/50 hover:shadow-sm transition-all text-left"
            data-testid="quick-charges-fixes">
            <div className="w-7 h-7 rounded-lg bg-[rgb(var(--lm-orange))]/10 flex items-center justify-center mb-2 group-hover:bg-[rgb(var(--lm-orange))]/20 transition-colors">
              <Wallet size={14} className="text-[rgb(var(--lm-orange))]" />
            </div>
            <p className="text-[11px] font-medium text-[rgb(var(--lm-text))]">Charges fixes</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{repartition ? formatCurrency(repartition.frais_fixes) : '...'}</p>
          </button>
          <button onClick={() => setActiveTab('salaires')}
            className="group p-3 rounded-xl bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 hover:border-[#5B8FE0]/50 hover:shadow-sm transition-all text-left"
            data-testid="quick-salaires">
            <div className="w-7 h-7 rounded-lg bg-[#5B8FE0]/10 flex items-center justify-center mb-2 group-hover:bg-[#5B8FE0]/20 transition-colors">
              <Users size={14} className="text-[#5B8FE0]" />
            </div>
            <p className="text-[11px] font-medium text-[rgb(var(--lm-text))]">Salaires</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{repartition ? formatCurrency(repartition.masse_salariale) : '...'}</p>
          </button>
        </div>
      </div>

      {/* ── Ratio BIMESTRIEL (lissé sur 2 mois) ── */}
      {ratioBim && ratioBim.bimestriel?.ca_ht > 0 && (
        <div className="lm-card p-5 animate-slide-up border-2 border-blue-400/30 bg-blue-500/5" style={{ animationDelay: '230ms', animationFillMode: 'backwards' }} data-testid="ratio-bimestriel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">📊</span>
              <div>
                <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))] font-bold">Ratio Bimestriel — le vrai pilotage</h3>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{ratioBim.mois_previous_label} + {ratioBim.mois_current_label} consolidés</p>
              </div>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${
              ratioBim.bimestriel.status === 'excellent' ? 'bg-emerald-500/20 text-emerald-400' :
              ratioBim.bimestriel.status === 'bon' ? 'bg-emerald-500/15 text-emerald-300' :
              ratioBim.bimestriel.status === 'attention' ? 'bg-amber-500/20 text-amber-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {ratioBim.bimestriel.status === 'excellent' ? '🎯 Excellent' : ratioBim.bimestriel.status === 'bon' ? '✅ Bon' : ratioBim.bimestriel.status === 'attention' ? '⚠️ Attention' : '🔴 Danger'}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 items-center">
            <div className="text-center">
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">{ratioBim.mois_previous_label}</p>
              <p className="font-mono text-xl text-[rgb(var(--lm-text-secondary))]">{ratioBim.previous?.ratio || 0}%</p>
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(ratioBim.previous?.ca_ht || 0)}</p>
            </div>
            <div className="text-center border-x border-blue-400/20 px-2">
              <p className="text-[10px] text-blue-400 uppercase font-bold">📊 Bimestriel</p>
              <p className="font-mono text-3xl font-black text-blue-400">{ratioBim.bimestriel.ratio}%</p>
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(ratioBim.bimestriel.ca_ht)}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">{ratioBim.mois_current_label}</p>
              <p className="font-mono text-xl text-[rgb(var(--lm-text-secondary))]">{ratioBim.current?.ratio || 0}%</p>
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(ratioBim.current?.ca_ht || 0)}</p>
            </div>
          </div>

          <div className="mt-3 p-2 bg-blue-500/10 rounded-lg">
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] leading-relaxed">
              💡 <strong>Lissage achats</strong> : Les achats fin de mois sont consommés le mois suivant. Le ratio bimestriel donne votre <strong>vrai food cost</strong>. Objectif : <strong>30-35%</strong>.
            </p>
          </div>
        </div>
      )}

      {/* ── Ratio Matieres (mensuel classique) ── */}
      {ratioData && ratioData.ca_ht > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '250ms', animationFillMode: 'backwards' }} data-testid="ratio-matieres">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Percent size={14} style={{ color: ratioData.status === 'danger' ? 'rgb(var(--lm-red))' : ratioData.status === 'attention' ? 'rgb(var(--lm-orange))' : 'rgb(var(--lm-green))' }} />
              <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Ratio Matieres</h3>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
              ratioData.status === 'excellent' ? 'bg-emerald-500/15 text-emerald-400' :
              ratioData.status === 'bon' ? 'bg-emerald-500/10 text-emerald-300' :
              ratioData.status === 'attention' ? 'bg-amber-500/15 text-amber-400' :
              'bg-red-500/15 text-red-400'
            }`}>
              {ratioData.status === 'excellent' ? 'Excellent' : ratioData.status === 'bon' ? 'Bon' : ratioData.status === 'attention' ? 'Attention' : 'Danger'}
            </span>
          </div>

          {/* Main ratio gauge */}
          <div className="flex items-center gap-6 mb-5">
            <div className="relative w-28 h-28 shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(var(--lm-border))" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none"
                  stroke={ratioData.ratio_global <= 30 ? 'rgb(var(--lm-green))' : ratioData.ratio_global <= 35 ? 'rgb(var(--lm-orange))' : 'rgb(var(--lm-red))'}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${Math.min(100, ratioData.ratio_global * 2.64)} 264`}
                />
                {/* Target line */}
                <circle cx="50" cy="50" r="42" fill="none"
                  stroke="rgb(var(--lm-gold))" strokeWidth="2" opacity="0.4"
                  strokeDasharray={`2 ${264 - 2}`}
                  strokeDashoffset={`-${ratioData.ratio_objectif * 2.64 - 1}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`font-mono text-2xl font-bold ${
                  ratioData.ratio_global <= 30 ? 'text-[rgb(var(--lm-green))]' : ratioData.ratio_global <= 35 ? 'text-[rgb(var(--lm-orange))]' : 'text-[rgb(var(--lm-red))]'
                }`}>{ratioData.ratio_global}%</span>
                <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">achats/CA</span>
              </div>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[rgb(var(--lm-text-secondary))]">CA HT</span>
                <span className="font-mono text-[rgb(var(--lm-gold))]">{formatCurrency(ratioData.ca_ht)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[rgb(var(--lm-text-secondary))]">Achats HT</span>
                <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(ratioData.achats_total_ht)}</span>
              </div>
              {ratioData.achats_cuisine_ht > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-[rgb(var(--lm-text-muted))]">Cuisine</span>
                  <span className="font-mono text-[rgb(var(--lm-text-muted))]">{formatCurrency(ratioData.achats_cuisine_ht)} ({ratioData.ratio_cuisine}%)</span>
                </div>
              )}
              {ratioData.achats_boissons_ht > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-[rgb(var(--lm-text-muted))]">Boissons</span>
                  <span className="font-mono text-[rgb(var(--lm-text-muted))]">{formatCurrency(ratioData.achats_boissons_ht)} ({ratioData.ratio_boissons}%)</span>
                </div>
              )}
              <div className="flex justify-between text-xs pt-1 border-t border-[rgb(var(--lm-border)/0.3)]">
                <span className="text-[rgb(var(--lm-text-muted))]">Objectif</span>
                <span className="font-mono text-[rgb(var(--lm-gold))]">{ratioData.ratio_objectif}%</span>
              </div>
            </div>
          </div>

          {/* Projection */}
          {ratioData.projection && ratioData.projection.ratio > 0 && ratioData.projection.ratio !== ratioData.ratio_global && (
            <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface-elevated))] mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target size={12} className="text-[rgb(var(--lm-gold))]" />
                  <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Projection fin de mois</span>
                </div>
                <span className={`font-mono text-sm font-medium ${
                  ratioData.projection.ratio <= 30 ? 'text-[rgb(var(--lm-green))]' : ratioData.projection.ratio <= 35 ? 'text-[rgb(var(--lm-orange))]' : 'text-[rgb(var(--lm-red))]'
                }`}>{ratioData.projection.ratio}%</span>
              </div>
              <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">
                <span>CA prevu: {formatCurrency(ratioData.projection.ca)}</span>
                <span>Achats prevus: {formatCurrency(ratioData.projection.achats)}</span>
              </div>
            </div>
          )}

          {/* Historique ratios mini chart */}
          {ratioData.historique?.filter(h => h.ratio > 0).length > 1 && (
            <div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">Evolution sur 6 mois</p>
              <ResponsiveContainer width="100%" height={80}>
                <BarChart data={[...ratioData.historique].reverse().filter(h => h.ratio > 0)} barSize={20}>
                  <XAxis dataKey="mois" tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 9 }} axisLine={false} tickLine={false}
                    tickFormatter={v => { const [,m] = v.split('-'); return ['','Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'][parseInt(m)]; }} />
                  <YAxis domain={[0, 50]} tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 9 }} axisLine={false} tickLine={false} width={24} tickFormatter={v => `${v}%`} />
                  <Tooltip
                    contentStyle={{ background: 'rgb(var(--lm-surface))', border: '1px solid rgb(var(--lm-border))', borderRadius: 2, fontSize: 11 }}
                    formatter={(v) => [`${v}%`, 'Ratio']}
                  />
                  <Bar dataKey="ratio" radius={[3, 3, 0, 0]}>
                    {[...ratioData.historique].reverse().filter(h => h.ratio > 0).map((entry, i) => (
                      <Cell key={i} fill={entry.ratio <= 30 ? 'rgb(var(--lm-green))' : entry.ratio <= 35 ? 'rgb(var(--lm-orange))' : 'rgb(var(--lm-red))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── Repartition des Charges ── */}
      {repartition && (repartition.ca_ht > 0 || repartition.objectif_ca > 0) && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '280ms', animationFillMode: 'backwards' }} data-testid="repartition-charges">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity size={14} className="text-[rgb(var(--lm-gold))]" />
              <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Repartition des charges</h3>
              {editingObjectif ? (
                <div className="flex items-center gap-1 ml-1">
                  <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">/ objectif</span>
                  <Input type="number" value={objectifCA} onChange={(e) => setObjectifCA(parseFloat(e.target.value) || 0)} className="h-5 w-24 text-[10px] px-1" data-testid="edit-objectif-input" />
                  <button onClick={async () => { await handleSaveObjectif(); setEditingObjectif(false); api.get(`/repartition-charges/${mois}`).then(r => setRepartition(r.data)); }} className="text-[rgb(var(--lm-green))] hover:text-emerald-300" data-testid="save-objectif-inline"><Check size={12} /></button>
                </div>
              ) : (
                <button onClick={() => setEditingObjectif(true)} className="text-[9px] text-[rgb(var(--lm-text-muted))] ml-1 hover:text-[rgb(var(--lm-gold))] flex items-center gap-1 cursor-pointer" data-testid="edit-objectif-btn">
                  / objectif {formatCurrency(repartition.objectif_ca || objectifCA)} <Percent size={8} />
                </button>
              )}
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
              repartition.pct_resultat >= 10 ? 'bg-emerald-500/15 text-emerald-400' :
              repartition.pct_resultat >= 0 ? 'bg-amber-500/15 text-amber-400' :
              'bg-red-500/15 text-red-400'
            }`}>
              {repartition.pct_resultat >= 10 ? 'Sain' : repartition.pct_resultat >= 0 ? 'Juste' : 'Deficit'}
            </span>
          </div>

          {/* Stacked bar */}
          <div className="mb-4">
            <div className="flex h-8 rounded-lg overflow-hidden">
              {repartition.pct_matieres > 0 && <div style={{ width: `${Math.min(repartition.pct_matieres, 100)}%` }} className="bg-blue-500 flex items-center justify-center" title="Matieres"><span className="text-[8px] text-white font-bold">{repartition.pct_matieres}%</span></div>}
              {repartition.pct_salaires > 0 && <div style={{ width: `${Math.min(repartition.pct_salaires, 100)}%` }} className="bg-purple-500 flex items-center justify-center" title="Salaires"><span className="text-[8px] text-white font-bold">{repartition.pct_salaires}%</span></div>}
              {repartition.pct_fixes > 0 && <div style={{ width: `${Math.min(repartition.pct_fixes, 100)}%` }} className="bg-orange-500 flex items-center justify-center" title="Frais fixes"><span className="text-[8px] text-white font-bold">{repartition.pct_fixes > 5 ? `${repartition.pct_fixes}%` : ''}</span></div>}
              {repartition.pct_resultat > 0 && <div style={{ width: `${Math.min(repartition.pct_resultat, 100)}%` }} className="bg-emerald-500 flex items-center justify-center" title="Resultat"><span className="text-[8px] text-white font-bold">{repartition.pct_resultat}%</span></div>}
            </div>
          </div>

          {/* Detail rows */}
          <div className="space-y-3">
            <ChargeRow label="Matieres premieres" value={repartition.matieres} pct={repartition.pct_matieres} target={30} color="bg-blue-500" />
            <ChargeRow label="Salaires + charges" value={repartition.masse_salariale} pct={repartition.pct_salaires} target={35} color="bg-purple-500" />
            {repartition.source_salaires === 'estimation' && (
              <div className="ml-5 -mt-0.5">
                <button onClick={() => setShowSalaires(!showSalaires)} className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="toggle-salaires-form">
                  <AlertTriangle size={10} /> Estimation — {showSalaires ? 'Masquer' : 'Saisir vos vrais salaires'}
                </button>
              </div>
            )}
            {!showSalaires && repartition.source_salaires !== 'estimation' && (
              <div className="ml-5 -mt-0.5">
                <button onClick={() => setShowSalaires(!showSalaires)} className="text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid="toggle-salaires-edit">
                  <Users size={10} /> {showSalaires ? 'Masquer' : 'Modifier les salaires'}
                </button>
              </div>
            )}
            {showSalaires && (
              <div className="ml-5 mt-2">
                <SalairesForm onSaved={() => {
                  api.get(`/repartition-charges/${mois}`).then(r => setRepartition(r.data)).catch(() => {});
                }} />
              </div>
            )}
            <ChargeRow label="Frais fixes" value={repartition.frais_fixes} pct={repartition.pct_fixes} target={25} color="bg-orange-500" />

            {/* Frais fixes detail toggle */}
            <RepartitionFixesDetail categories={repartition.fixes_categories} />

            <div className="flex items-center justify-between pt-2 border-t border-[rgb(var(--lm-border)/0.3)]">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${repartition.pct_resultat >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <span className="text-sm font-medium text-[rgb(var(--lm-text))]">Resultat {repartition.objectif_ca > 0 ? 'projete' : 'net'}</span>
              </div>
              <div className="text-right">
                <span className={`font-mono text-lg font-bold ${repartition.pct_resultat >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                  {formatCurrency(repartition.resultat_net)}
                </span>
                <span className={`text-xs ml-2 ${repartition.pct_resultat >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                  {repartition.pct_resultat}%
                </span>
                <span className="text-[9px] text-[rgb(var(--lm-text-muted))] ml-1">obj. 10%</span>
              </div>
            </div>
            {repartition.objectif_ca > 0 && repartition.ca_ht > 0 && (
              <div className="ml-5 mt-1 text-[10px] text-[rgb(var(--lm-text-muted))]">
                CA realise : {formatCurrency(repartition.ca_ht)} / {formatCurrency(repartition.objectif_ca)} ({Math.round(repartition.ca_ht / repartition.objectif_ca * 100)}%)
              </div>
            )}

            {/* Verdict Rentabilite — base charges fixes */}
            {repartition.charges_fixes_totales > 0 && (
              <div className={`p-4 rounded-xl mt-3 border ${
                repartition.verdict === 'excellent' ? 'bg-emerald-500/8 border-emerald-500/20' :
                repartition.verdict === 'bon' ? 'bg-blue-500/8 border-blue-500/20' :
                repartition.verdict === 'attention' ? 'bg-amber-500/8 border-amber-500/20' :
                'bg-red-500/8 border-red-500/20'
              }`} data-testid="verdict-rentabilite">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Target size={14} className={
                      repartition.verdict === 'excellent' ? 'text-emerald-400' :
                      repartition.verdict === 'bon' ? 'text-blue-400' :
                      repartition.verdict === 'attention' ? 'text-amber-400' : 'text-red-400'
                    } />
                    <span className="text-xs font-medium text-[rgb(var(--lm-text))]">Verdict charges fixes</span>
                  </div>
                  <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${
                    repartition.verdict === 'excellent' ? 'bg-emerald-500/20 text-emerald-400' :
                    repartition.verdict === 'bon' ? 'bg-blue-500/20 text-blue-400' :
                    repartition.verdict === 'attention' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>{
                    repartition.verdict === 'excellent' ? 'Excellent' :
                    repartition.verdict === 'bon' ? 'Bon' :
                    repartition.verdict === 'attention' ? 'Attention' : 'Danger'
                  }</span>
                </div>

                {/* Base fixe */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-surface))]">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Salaires (base fixe)</p>
                    <p className="font-mono text-sm font-bold text-purple-400">{formatCurrency(repartition.base_salaires_fixe)}</p>
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">
                      {repartition.source_base_salaires === 'bulletins' ? 'Bulletins mois prec.' :
                       repartition.source_base_salaires === 'salaires_mensuels' ? 'Salaires mois prec.' : 'Base actuelle'}
                    </p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-surface))]">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Total charges fixes</p>
                    <p className="font-mono text-sm font-bold text-orange-400">{formatCurrency(repartition.charges_fixes_totales)}</p>
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">Salaires + frais fixes</p>
                  </div>
                </div>

                {/* Jauge % charges fixes / CA */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[rgb(var(--lm-text-secondary))]">Charges fixes / CA actuel</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-sm font-bold ${
                        repartition.pct_charges_fixes <= 60 ? 'text-emerald-400' :
                        repartition.pct_charges_fixes <= 70 ? 'text-amber-400' : 'text-red-400'
                      }`}>{repartition.pct_charges_fixes}%</span>
                      {repartition.ca_projete > 0 && (
                        <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">
                          (projete: {Math.round(repartition.charges_fixes_totales / repartition.ca_projete * 100)}%)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-[rgb(var(--lm-surface))] overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${
                      repartition.pct_charges_fixes <= 60 ? 'bg-emerald-500' :
                      repartition.pct_charges_fixes <= 70 ? 'bg-amber-500' : 'bg-red-500'
                    }`} style={{ width: `${Math.min(repartition.pct_charges_fixes, 100)}%` }} />
                  </div>
                  <div className="flex justify-between mt-0.5">
                    <span className="text-[8px] text-emerald-400">Objectif &lt;60%</span>
                    <span className="text-[8px] text-amber-400">60-70%</span>
                    <span className="text-[8px] text-red-400">&gt;70%</span>
                  </div>
                </div>

                {/* Projection et CA necessaire */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-surface))]">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">CA projete fin mois</p>
                    <p className={`font-mono text-sm font-bold ${repartition.ca_projete >= repartition.ca_cible_equilibre ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {formatCurrency(repartition.ca_projete)}
                    </p>
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">Base {formatCurrency(repartition.ca_par_jour)}/jour</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-surface))]">
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">CA cible equilibre</p>
                    <p className="font-mono text-sm font-bold text-[rgb(var(--lm-gold))]">{formatCurrency(repartition.ca_cible_equilibre)}</p>
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">Pour couvrir charges fixes</p>
                  </div>
                </div>

                {/* Message actionnable */}
                {repartition.jours_restants > 0 && repartition.ca_manquant_equilibre > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border)/0.3)]">
                    <p className="text-xs text-[rgb(var(--lm-text))]">
                      <span className="font-bold">Il reste {repartition.jours_restants} jours</span> — il faut faire{' '}
                      <span className="font-mono font-bold text-[rgb(var(--lm-gold))]">{formatCurrency(repartition.ca_jour_necessaire)}/jour</span>{' '}
                      pour couvrir vos charges fixes
                    </p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">
                      Soit {formatCurrency(repartition.ca_manquant_equilibre)} de CA supplementaire
                    </p>
                  </div>
                )}
                {repartition.jours_restants > 0 && repartition.ca_manquant_equilibre <= 0 && (
                  <div className="mt-3 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-xs text-emerald-400 font-medium">
                      Charges fixes couvertes ! Vous etes dans les clous.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Rapport Mensuel ── */}
      {repartition && (repartition.ca_ht > 0 || repartition.objectif_ca > 0) && (
        <RapportMensuel mois={mois} moisLabel={`${MONTHS_FR[mm]} ${yy}`} />
      )}

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* CA Evolution */}
        {caChartData.length > 1 && (
          <div className="lg:col-span-2 lm-card p-5 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }} data-testid="ca-chart">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
                <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Evolution CA & resultat</h3>
              </div>
              <div className="flex gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgb(var(--lm-gold))' }} /> CA</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgb(var(--lm-green))' }} /> Resultat</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={caChartData} barGap={3} barSize={18}>
                <XAxis dataKey="mois" tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 10, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgb(var(--lm-text-muted))', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={32} tickFormatter={v => `${v}k`} />
                <Tooltip
                  contentStyle={{ background: 'rgb(var(--lm-surface))', border: '1px solid rgb(var(--lm-border))', borderRadius: 2, fontSize: 12, fontFamily: 'Manrope' }}
                  labelStyle={{ color: 'rgb(var(--lm-gold))' }}
                  formatter={(v, name) => [`${v}k€`, name === 'ca' ? 'CA' : name === 'depenses' ? 'Depenses' : 'Resultat']}
                />
                <Bar dataKey="ca" fill="rgb(var(--lm-gold))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="resultat" fill="rgb(var(--lm-green))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Depenses Pie */}
        {depensesPie.length > 0 && (
          <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }} data-testid="depenses-pie">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart size={14} style={{ color: 'rgb(var(--lm-orange))' }} />
              <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Depenses</h3>
            </div>
            <div className="flex justify-center mb-3">
              <ResponsiveContainer width={130} height={130}>
                <PieChart>
                  <Pie data={depensesPie} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="value" stroke="none" paddingAngle={2}>
                    {depensesPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {depensesPie.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[rgb(var(--lm-text-secondary))]">{d.name}</span>
                  </div>
                  <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Objectif & Budget ── */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards' }}>
        <div className="flex items-center gap-2 mb-5">
          <Wallet size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Objectif & Budget</h3>
        </div>
        <div className="space-y-5">
          {/* Objectif CA */}
          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Objectif CA mensuel HT</label>
            <div className="flex gap-2">
              <Input type="number" value={objectifCA} onChange={(e) => setObjectifCA(parseFloat(e.target.value) || 0)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-[rgb(var(--lm-text))]" placeholder="Ex: 45000" data-testid="objectif-ca-input" />
              <Button onClick={handleSaveObjectif} size="sm" variant="outline"
                className="border-[rgb(var(--lm-gold))] text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]">OK</Button>
            </div>
          </div>

          {/* Ratio selectors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Mon ratio (Patron)</label>
              <div className="flex items-center gap-2">
                {[25, 28, 30, 33, 35].map(r => (
                  <button key={r} onClick={() => { setRatioPatron(r); handleSaveRatios(r, ratioChef); }}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-all ${
                      ratioPatron === r
                        ? 'bg-lemas-gold text-[rgb(var(--lm-bg))] font-bold'
                        : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'
                    }`} data-testid={`ratio-patron-${r}`}>{r}%</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Ratio Chef (serre)</label>
              <div className="flex items-center gap-2">
                {[18, 20, 22, 25].map(r => (
                  <button key={r} onClick={() => { setRatioChef(r); handleSaveRatios(ratioPatron, r); }}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-mono transition-all ${
                      ratioChef === r
                        ? 'bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] font-bold'
                        : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'
                    }`} data-testid={`ratio-chef-${r}`}>{r}%</button>
                ))}
              </div>
            </div>
          </div>

          {/* Budgets auto-calculés */}
          {objectifCA > 0 && (
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-[rgb(var(--lm-bg))]/50">
              <div className="text-center">
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Budget reel ({ratioPatron}%)</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(objectifCA * ratioPatron / 100)}</p>
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-0.5">Votre suivi</p>
              </div>
              <div className="text-center">
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1">Budget Chef ({ratioChef}%)</p>
                <p className="font-mono text-lg" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(objectifCA * ratioChef / 100)}</p>
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-0.5">Visible par le Chef</p>
              </div>
            </div>
          )}
        </div>
        {objectifCA > 0 && a && (
          <div className="mt-5">
            <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">
              <span>Progression</span>
              <span className="font-mono">{progressPct.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-[rgb(var(--lm-surface-elevated))] rounded-full h-1.5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progressPct}%`, background: 'rgb(var(--lm-gold))' }} />
            </div>
            <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))] mt-1.5 font-mono">
              <span>{formatCurrency(a.ca_ht)}</span>
              <span>{formatCurrency(objectifCA)}</span>
            </div>
          </div>
        )}
        {/* Budget History */}
        {budgetHistory.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[rgb(var(--lm-border)/0.3)]">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">Evolution budget</p>
            <div className="space-y-1.5">
              {budgetHistory.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-[rgb(var(--lm-text-muted))] font-mono w-[60px] shrink-0">{new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                  <span className="font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(h.ancien)}</span>
                  <span className="text-[rgb(var(--lm-text-muted))]">&rarr;</span>
                  <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(h.nouveau)}</span>
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${h.ecart > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                    {h.ecart > 0 ? '+' : ''}{formatCurrency(h.ecart)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── N-1 Comparison ── */}
      {n1Data?.has_n1_data && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '420ms', animationFillMode: 'backwards' }} data-testid="n1-comparison">
          <div className="flex items-center gap-2 mb-5">
            <CalendarDays size={14} style={{ color: 'rgb(var(--lm-text-secondary))' }} />
            <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">
              vs {n1Data.annee_n1} {n1Data.a_date ? `(a date — J${n1Data.cutoff_day})` : ''}
            </h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'CA HT', n: n1Data.ca.n, n1: n1Data.ca.n1, pct: n1Data.ca.ecart_pct, format: true },
              { label: 'Couverts', n: n1Data.couverts.n, n1: n1Data.couverts.n1, pct: n1Data.couverts.ecart_pct },
              { label: 'Ticket moyen', n: n1Data.ticket_moyen.n, n1: n1Data.ticket_moyen.n1, pct: n1Data.ticket_moyen.ecart_pct, format: true },
              { label: 'Achats', n: n1Data.achats_cuisine.n, n1: n1Data.achats_cuisine.n1, pct: n1Data.achats_cuisine.ecart_pct, format: true, invert: true },
            ].map((item, i) => (
              <div key={i} className="lm-card-flat p-4">
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">{item.label}</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{item.format ? formatCurrency(item.n) : item.n}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Delta value={item.pct} invert={item.invert} />
                  <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{item.format ? formatCurrency(item.n1) : item.n1}</span>
                </div>
              </div>
            ))}
          </div>
          {n1Data.congelateur && (n1Data.congelateur.congele > 0 || n1Data.congelateur.utilise > 0) && (
            <div className="mt-3 flex items-center gap-3 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/15" data-testid="congelateur-impact">
              <Snowflake size={14} className="text-blue-400 shrink-0" />
              <div className="flex items-center gap-3 text-[10px] font-mono flex-wrap">
                {n1Data.congelateur.congele > 0 && (
                  <span className="text-blue-400">Congelé: -{formatCurrency(n1Data.congelateur.congele)}</span>
                )}
                {n1Data.congelateur.utilise > 0 && (
                  <span className="text-green-400">Remis: +{formatCurrency(n1Data.congelateur.utilise)}</span>
                )}
                <span className={`font-medium ${n1Data.congelateur.impact >= 0 ? 'text-orange-400' : 'text-green-400'}`}>
                  Impact: {n1Data.congelateur.impact >= 0 ? '+' : ''}{formatCurrency(n1Data.congelateur.impact)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Pertes ── */}
      {(a?.pertes > 0 || losses.length > 0) && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '480ms', animationFillMode: 'backwards' }} data-testid="pertes-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingDown size={14} style={{ color: 'rgb(var(--lm-red))' }} />
              <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Pertes du mois</h3>
            </div>
            <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-red))' }}>{formatCurrency(a?.pertes || dashboard?.total_pertes || 0)}</span>
          </div>
          {losses.length > 0 && (
            <div className="space-y-1">
              {Object.entries(losses.reduce((acc, l) => {
                acc[l.produit] = (acc[l.produit] || 0) + l.montant;
                return acc;
              }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([nom, total]) => (
                <div key={nom} className="flex justify-between items-center text-sm py-1.5 px-3 rounded-sm" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
                  <span className="text-[rgb(var(--lm-text-secondary))] truncate">{nom}</span>
                  <span className="font-mono text-[rgb(var(--lm-red))]">{formatCurrency(total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Propositions Traiteur ── */}
      <PropositionsWidget />

      {/* ── Roue du Mas ── */}
      <RoueWidget />
    </div>
  );
};

/* ── Propositions Traiteur Widget ── */
const PropositionsWidget = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/propositions/stats/dashboard`)
      .then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  if (!stats || stats.total === 0) return null;

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
  const statutColors = { brouillon: 'rgb(var(--lm-text-muted))', envoyee: '#3b82f6', relance: '#f59e0b', acceptee: 'rgb(var(--lm-green))', refusee: 'rgb(var(--lm-red))' };
  const statutLabels = { brouillon: 'Brouillon', envoyee: 'Envoyée', relance: 'Relance', acceptee: 'Acceptée', refusee: 'Refusée' };

  return (
    <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '500ms', animationFillMode: 'backwards' }} data-testid="propositions-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Utensils size={14} style={{ color: '#009893' }} />
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Propositions Traiteur</h3>
        </div>
        <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{stats.total} total</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-lg" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">CA Potentiel</p>
          <p className="font-mono text-lg" style={{ color: '#009893' }} data-testid="prop-ca-potentiel">{fmt(stats.ca_potentiel)}</p>
        </div>
        <div className="p-3 rounded-lg" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">CA Confirmé</p>
          <p className="font-mono text-lg text-[rgb(var(--lm-green))]" data-testid="prop-ca-confirme">{fmt(stats.ca_confirme)}</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-3">
        {Object.entries(stats.compteurs).filter(([, v]) => v > 0).map(([k, v]) => (
          <span key={k} className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ background: `${statutColors[k]}15`, color: statutColors[k] }}>
            {statutLabels[k]} : {v}
          </span>
        ))}
      </div>

      {stats.alertes_relance?.length > 0 && (
        <div className="space-y-1 mb-3">
          {stats.alertes_relance.slice(0, 3).map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] text-[rgb(var(--lm-text-muted))] p-1.5 rounded" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
              <AlertTriangle size={10} className="text-amber-500 shrink-0" />
              <span className="truncate">{a.client} — {a.jours}j sans réponse</span>
              <span className="font-mono ml-auto shrink-0">{fmt(a.montant)}</span>
            </div>
          ))}
        </div>
      )}

      {stats.prochains_7j?.length > 0 && (
        <div className="space-y-1">
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Prochains événements</p>
          {stats.prochains_7j.slice(0, 3).map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] p-1.5 rounded" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
              <CalendarDays size={10} className="text-[rgb(var(--lm-green))] shrink-0" />
              <span className="text-[rgb(var(--lm-text))] truncate">{e.client} — {e.type}</span>
              <span className="font-mono text-[rgb(var(--lm-text-muted))] ml-auto shrink-0">{new Date(e.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Roue du Mas Widget ── */
const RoueWidget = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/roue/stats').then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return null;
  const { total_clients, total_vouchers, used_vouchers, taux_utilisation, prize_distribution } = stats;
  const maxP = Math.max(...(prize_distribution || []).map(p => p.count), 1);
  return (
    <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '520ms', animationFillMode: 'backwards' }} data-testid="roue-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CircleDot size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Roue du Mas</h3>
        </div>
        <a href="/roue" target="_blank" rel="noreferrer"
          className="text-[10px] text-[rgb(var(--lm-gold))] hover:underline">Ouvrir →</a>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 rounded-lg" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">Clients</p>
          <p className="font-mono text-lg text-[rgb(var(--lm-gold))]" data-testid="roue-clients">{total_clients}</p>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">Bons</p>
          <p className="font-mono text-lg text-[rgb(var(--lm-text))]" data-testid="roue-vouchers">{total_vouchers}</p>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ background: 'rgb(var(--lm-surface-elevated))' }}>
          <p className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">Utilises</p>
          <p className="font-mono text-lg text-[rgb(var(--lm-green))]" data-testid="roue-taux">{taux_utilisation}%</p>
        </div>
      </div>
      {prize_distribution?.length > 0 && (
        <div className="space-y-1.5">
          {prize_distribution.map(p => (
            <div key={p.prize_id} className="flex items-center gap-2 text-xs">
              <span className="w-4">{p.emoji}</span>
              <span className="text-[rgb(var(--lm-text-muted))] w-28 truncate">{p.label}</span>
              <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgb(var(--lm-border))' }}>
                <div className="h-full rounded-full" style={{ width: `${(p.count / maxP) * 100}%`, background: 'rgb(var(--lm-gold))' }} />
              </div>
              <span className="font-mono text-[rgb(var(--lm-text-muted))] w-5 text-right">{p.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatronDashboard;
